import classNames from 'classnames';
import React, { useContext, useRef } from 'react';
import { Popover, Tooltip } from '@meitu/saas-ui';
import { getTextMarkConfig, type TextMarkStruct } from '@meitu/saas-editor-core/es/layers/text-layer';
import { imageConverter } from '@meitu/saas-editor-core/es/utils/file-kit';
import { TextWritingType, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { EditorContext, useEditor } from '@/hooks/use-editor';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useLocaleStore } from '@/i18n';
import { TextPanelContext } from '../../context';
import { useTextMark } from '../../hooks';
import { TextMarkPanel } from '../../panels';
import { useStore } from '../../store';
import { getTextMarkThumbnailURL } from '../../utils';
import './style.less';

type Props = React.ComponentProps<'div'> & {};

export const TextMarkEntry: React.FC<Props> = ({ style }) => {
    const context = useContext(TextPanelContext)!;
    const { theme, isIntl } = context;

    const { $t } = useLocaleStore();

    const editor = useEditor();

    const popoverRef = useRef<any>(null);

    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { writingMode, shapeExtension } = layerData;

        return {
            writingMode,
            shapeExtension,
        };
    });

    const textMarkMaterialsRef = useRef<TextMarkStruct[]>(getTextMarkConfig());

    const shapeExtensionCache = useStore((state) => state.shapeExtensionCache);

    const writingMode = props ? props.writingMode : TextWritingType.HORIZONTAL_TB;
    const shapeExtension = props ? props.shapeExtension : null;

    const disableTip = (() => {
        if (shapeExtension || shapeExtensionCache) {
            return $t((v) => v.textMarkSelector.disabledByTransform);
        }

        if (writingMode !== TextWritingType.HORIZONTAL_TB) {
            return $t((v) => v.textMarkSelector.disabledByWritingMode);
        }
        return '';
    })();

    const { hasNoTextMark, hasMultipleTextMarks, textMark, textMarksInTextContents } = useTextMark(editor);

    const targetTextMark = (() => {
        if (textMarksInTextContents.length === 1) return textMarksInTextContents[0];
        return textMark;
    })();

    const DEFAULT_TEXT_MARK_DATA = textMarkMaterialsRef.current[0];
    const DEFAULT_TEXT_MARK_THUMBNAIL_URL = DEFAULT_TEXT_MARK_DATA
        ? getTextMarkThumbnailURL(DEFAULT_TEXT_MARK_DATA, theme, isIntl)
        : '';

    const targetTextMarkData = targetTextMark
        ? textMarkMaterialsRef.current.find((item) => String(item.materialId) === String(targetTextMark.materialId))
        : null;

    const thumbnailURL = !targetTextMarkData ? '' : getTextMarkThumbnailURL(targetTextMarkData, theme, isIntl);

    const rootCls = classNames('modal-panel-entry', { 'modal-panel-entry--disabled': disableTip !== '' });

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disableTip) return e.stopPropagation();
    };

    return (
        <Popover
            trigger="click"
            placement="left"
            ref={popoverRef}
            content={
                <EditorContext.Provider value={editor}>
                    <TextPanelContext.Provider value={context}>
                        <TextMarkPanel
                            onClose={() => {
                                // 模拟弹窗外点击关闭弹窗
                                popoverRef.current?.onClick?.();
                            }}
                        />
                    </TextPanelContext.Provider>
                </EditorContext.Provider>
            }
            overlayClassName="text-layer-panel-popover"
            destroyTooltipOnHide
        >
            <Tooltip title={disableTip}>
                <div className={rootCls} onClickCapture={handleClick} style={style}>
                    <div className="modal-panel-entry__thumbnail">
                        {hasNoTextMark ? (
                            <div
                                className="sub-panel-entry__cover"
                                style={{
                                    backgroundImage: `url(${DEFAULT_TEXT_MARK_THUMBNAIL_URL ? imageConverter(DEFAULT_TEXT_MARK_THUMBNAIL_URL, 116) : ''})`,
                                }}
                            />
                        ) : null}
                        {hasMultipleTextMarks ? <div className="modal-panel-entry__dots">···</div> : null}
                        {targetTextMark && thumbnailURL && !hasMultipleTextMarks ? (
                            <div
                                className="sub-panel-entry__cover"
                                style={{
                                    backgroundImage: `url(${imageConverter(thumbnailURL, 116)})`,
                                }}
                            />
                        ) : null}
                    </div>
                    <div className="modal-panel-entry__title">{$t((v) => v.textMarkSelector.mark)}</div>
                </div>
            </Tooltip>
        </Popover>
    );
};
